// Native & external imports
import { Injectable, inject } from "@angular/core";
import { OwlDateTimeFormats } from "@danielmoncada/angular-datetime-picker";
// Services
import { AppLoadService } from './app-load.service';
import { AppLocalization } from '@icamppro-customer-portal/shared/cmp-base';
// Objects
import { CmpAppConfigObject } from "src/objects/cmp-app-config.object";

/**
 * Used as a factory for injection of `OwlDateTimeFormats` object
 */
export const SET_CUSTOM_DATE_FORMAT = (
  cmpAppSettings: CmpAppSettings,
): OwlDateTimeFormats => cmpAppSettings.pickerDateTimeFormats;

@Injectable({ providedIn: "root" })
export class CmpAppSettings {
  private _appLoadService = inject(AppLoadService);
  private _appLocalization = inject(AppLocalization);

  private _pickerDateTimeFormats: OwlDateTimeFormats;
  public get pickerDateTimeFormats(): Readonly<OwlDateTimeFormats> {
    return this._pickerDateTimeFormats;
  }

  public get fullPickerPlaceholder(): string {
    return (
      this.appLocalization.client.getLocalizedDateFormat(
        this.pickerDateTimeFormats.fullPickerInput,
      ) || ""
    );
  }

  public get datePickerPlaceholder(): string {
    return (
      this.appLocalization.client.getLocalizedDateFormat(
        this.pickerDateTimeFormats.datePickerInput,
      ) || ""
    );
  }

  public get timePickerPlaceholder(): string {
    return (
      this.appLocalization.client.getLocalizedDateFormat(
        this.pickerDateTimeFormats.timePickerInput,
      ) || ""
    );
  }

  public get appLocalization(): AppLocalization {
    return this._appLocalization;
  }

  public get currencySymbol(): string {
    return (
      this.appLocalization.currencySymbolFromName(
        this.getAppSetting("currencyName"),
      ) || "$"
    );
  }

  public httpUrlFormatConvert(url: string) {
    let newUrl = url;

    if (typeof url !== "undefined" && !/^(ht)tps?:\/\//i.test(url)) {
      newUrl = `http://${url}`;
    }

    return newUrl;
  }

  constructor() {
    // We no longer read/use dateFormat from settings because the the
    // localized date formats (L, LL, LLL, etc.) will always be
    // correct for the client, and we should localize for the client
    // not the account.
    this._pickerDateTimeFormats = {
      fullPickerInput: "L LT",
      datePickerInput: "L",
      timePickerInput: "LT",
      monthYearLabel: "MMM YYYY",
      dateA11yLabel: "L",
      monthYearA11yLabel: "MMMM YYYY",
      parseInput: undefined,
    };
  }

  public getAppSetting(field: keyof CmpAppConfigObject): string {
    return (
      this._appLoadService.appSettings[field] ||
      this.getDefaultAppSetting(field)
    );
  }

  private getDefaultAppSetting(field: keyof CmpAppConfigObject): string {
    switch (field) {
      case "appName":
      case "appTitle":
      case "companyName":
        return "iCampPro";
      case "currencyName":
        return "USD";
      case "locale":
        return "en-US";
      case "timeZone":
        return "America/Chicago";
      default:
        return "";
    }
  }

  public displayCurrency(amount: number | string): string {
    if (typeof amount === "string") {
      amount = this._appLocalization.client.stringToNumber(amount) || 0;
    }
    return this.appLocalization.client.formatCurrency(amount);
  }

  public getMainSettings() {
    return this._appLoadService.appMaintenanceSettings;
  }
}
